<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <!-- Section Type de structure (transformation libéral viceversa MSP) -->
    <v-card outlined class="mt-3 mb-3">
      <v-card-title class="section-title">
        <v-icon left small color="#9c27b0">mdi-domain</v-icon>
        Type de structure
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-radio-group
          v-model="form.partyType"
          row
          dense
          hide-details
          class="mt-0"
        >
          <v-radio value="liberal" label="Libéral"></v-radio>
          <v-radio value="msp" label="MSP / Centre de santé"></v-radio>
        </v-radio-group>
        <div v-if="isPartyTransform" class="party-transform-hint mt-2">
          <v-icon small color="orange darken-2">mdi-swap-horizontal</v-icon>
          <span>
            Transformation
            <strong>{{ partyTypeLabel(initialPartyType) }}</strong>
            vers
            <strong>{{ partyTypeLabel(form.partyType) }}</strong>
            : re-signature médecin requise, montant et type de prestation
            recalculés côté serveur.
          </span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Section Bon de commande -->
    <v-card outlined class="mt-3 mb-3">
      <v-card-title class="section-title">
        <v-icon left small color="#e2007a"
          >mdi-file-document-edit-outline</v-icon
        >
        Bon de commande
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              :value="montantAfficheHT"
              label="Montant SEGUR HT"
              :suffix="isPartyTransform ? '' : '€'"
              outlined
              dense
              readonly
              :hint="montantHint"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="form.orderForm.typePrestation"
              :items="typePrestationOptions"
              :label="typePrestationLabel"
              outlined
              dense
              :disabled="lockTypePrestation"
              :hint="typePrestationHint"
              :persistent-hint="!!typePrestationHint"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.orderForm.dateSignatureCommande"
                  label="Date signature commande"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                  :rules="rules.dateSignatureCommande"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.orderForm.dateSignatureCommande"
                @input="dateMenu = false"
                locale="fr-FR"
                first-day-of-week="1"
                :max="todayIso"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-text-field
                    v-model="form.orderForm.codePostalMedecin"
                    label="Code postal médecin (TVA)"
                    outlined
                    dense
                    maxlength="5"
                    :rules="rules.codePostal"
                    append-icon="mdi-information-outline"
                  ></v-text-field>
                </div>
              </template>
              <span>Source TVA - distinct de l'adresse cabinet (zip)</span>
            </v-tooltip>
          </v-col>
        </v-row>

        <!-- Hint sync TVA / cabinet : signal subtil quand les 2 codes postaux
             diffèrent, avec boutons de copie unidirectionnels. La distinction
             est légitime (médecin DOM avec cabinet métropole, etc.) mais souvent
             une saisie incomplète à corriger. -->
        <v-row v-if="codesPostauxDiffer" dense>
          <v-col cols="12">
            <div class="codes-postaux-mismatch">
              <v-icon small color="orange darken-2"
                >mdi-information-outline</v-icon
              >
              <span class="ml-1">
                Code postal TVA (<strong>{{
                  form.orderForm.codePostalMedecin
                }}</strong
                >) différent du code postal cabinet (<strong>{{
                  form.resident.zip
                }}</strong
                >). Légitime si facturation et cabinet à des adresses
                différentes, sinon :
              </span>
              <div class="mt-1">
                <v-btn
                  x-small
                  outlined
                  color="orange darken-2"
                  @click="copyTvaVersCabinet"
                >
                  <v-icon left x-small>mdi-arrow-right</v-icon>
                  Copier TVA vers cabinet
                </v-btn>
                <v-btn
                  x-small
                  outlined
                  color="orange darken-2"
                  class="ml-2"
                  @click="copyCabinetVersTva"
                >
                  <v-icon left x-small>mdi-arrow-left</v-icon>
                  Copier cabinet vers TVA
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Aides de minimis : éditable pour libéral, masqué pour MSP. Réactif au toggle. -->
        <template v-if="form.partyType === 'liberal'">
          <v-divider class="my-2"></v-divider>
          <v-row dense>
            <v-col cols="12">
              <fieldset class="aides-fieldset">
                <legend>Aides de minimis (libéral)</legend>
                <v-radio-group
                  v-model="form.orderForm.aidesPercuesBool"
                  row
                  dense
                  hide-details
                  class="mt-0"
                >
                  <v-radio :value="false" label="Aucune aide perçue"></v-radio>
                  <v-radio
                    :value="true"
                    label="Aides perçues sur 3 ans"
                  ></v-radio>
                </v-radio-group>
                <div
                  v-if="form.orderForm.aidesPercuesBool === true"
                  class="mt-3"
                >
                  <aides-de-minimis-editor
                    v-model="form.orderForm.aidesDeMinimis"
                  />
                </div>
                <div v-else class="aides-disabled-note">
                  Le tableau des aides est masqué tant que l'option « Aides
                  perçues » est décochée.
                </div>
              </fieldset>
            </v-col>
          </v-row>
        </template>
      </v-card-text>
    </v-card>

    <!-- Section Médecin -->
    <v-card outlined class="mb-3">
      <v-card-title class="section-title">
        <v-icon left small color="#2196f3">mdi-account-edit-outline</v-icon>
        Médecin
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row dense>
          <!-- RPPS / nom / prénom : figés par la CPS -->
          <v-col cols="12" sm="6" md="4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-text-field
                    :value="bdc && bdc.resident && bdc.resident.RPPS"
                    label="RPPS"
                    readonly
                    disabled
                    outlined
                    dense
                    append-icon="mdi-lock-outline"
                  ></v-text-field>
                </div>
              </template>
              <span>Champ figé par la CPS</span>
            </v-tooltip>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-text-field
                    :value="bdc && bdc.resident && bdc.resident.nom"
                    label="Nom"
                    readonly
                    disabled
                    outlined
                    dense
                    append-icon="mdi-lock-outline"
                  ></v-text-field>
                </div>
              </template>
              <span>Champ figé par la CPS</span>
            </v-tooltip>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-text-field
                    :value="bdc && bdc.resident && bdc.resident.prénom"
                    label="Prénom"
                    readonly
                    disabled
                    outlined
                    dense
                    append-icon="mdi-lock-outline"
                  ></v-text-field>
                </div>
              </template>
              <span>Champ figé par la CPS</span>
            </v-tooltip>
          </v-col>

          <!-- Champs éditables -->
          <v-col cols="12" sm="6" md="6">
            <v-text-field
              v-model="form.resident.email"
              label="Email"
              type="email"
              outlined
              dense
              :rules="rules.email"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <v-text-field
              v-model="form.resident.phone"
              label="Téléphone"
              outlined
              dense
              :rules="rules.phone"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.resident.address"
              label="Adresse cabinet (rue)"
              outlined
              dense
              :rules="rules.address"
            ></v-text-field>
          </v-col>
          <v-col cols="6" sm="3" md="3">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-text-field
                    v-model="form.resident.zip"
                    label="Code postal adresse"
                    outlined
                    dense
                    maxlength="5"
                    :rules="rules.codePostal"
                    append-icon="mdi-information-outline"
                  ></v-text-field>
                </div>
              </template>
              <span
                >Adresse cabinet - distinct du code postal médecin (TVA)</span
              >
            </v-tooltip>
          </v-col>
          <v-col cols="6" sm="3" md="3">
            <v-text-field
              v-model="form.resident.city"
              label="Ville"
              outlined
              dense
              :rules="rules.city"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Section Structure (visible si type MSP) -->
    <v-card v-if="form.partyType === 'msp'" outlined class="mb-3">
      <v-card-title class="section-title">
        <v-icon left small color="#9c27b0">mdi-office-building-outline</v-icon>
        Structure
        <v-chip
          v-if="mspChecked && mspFound"
          x-small
          color="green darken-1"
          dark
          class="ml-2"
        >
          <v-icon left x-small>mdi-check</v-icon>
          MSP reconnue
        </v-chip>
        <v-chip
          v-else-if="mspChecked && !mspFound"
          x-small
          color="grey"
          outlined
          class="ml-2"
        >
          <v-icon left x-small>mdi-help-circle-outline</v-icon>
          Non trouvée
        </v-chip>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="form.msp.FINESS"
              :label="finessLabel('FINESS')"
              outlined
              dense
              maxlength="9"
              :rules="isPartyTransform ? rules.finessRequired : rules.finess"
              :loading="finessLoading"
              :hint="
                isPartyTransform ? 'Saisissez 9 chiffres pour pré-remplir' : ''
              "
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="form.msp.FINESSGEO"
              :label="finessLabel('FINESS géographique')"
              outlined
              dense
              maxlength="9"
              :rules="isPartyTransform ? rules.finessRequired : rules.finess"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model.number="form.msp.effectifRef"
              :label="finessLabel('Effectif (nb praticiens)')"
              type="number"
              min="1"
              outlined
              dense
              :rules="
                isPartyTransform ? rules.effectifRequired : rules.effectif
              "
              hint="Détermine la tranche tarifaire (A à E)"
              persistent-hint
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.msp.raisonSociale"
              :label="finessLabel('Raison sociale')"
              outlined
              dense
              :rules="
                isPartyTransform
                  ? rules.raisonSocialeRequired
                  : rules.raisonSociale
              "
            ></v-text-field>
          </v-col>
          <v-col cols="12" v-if="isPartnerRefEnabled && !isPartyTransform">
            <v-text-field
              v-model="form.msp.partnerRef"
              label="Référence interne (partenaire)"
              maxlength="64"
              outlined
              dense
              hint="Optionnel : identifiant interne partenaire, non transmis à l'ASP"
              persistent-hint
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Actions -->
    <div class="d-flex justify-end" style="gap: 8px">
      <v-btn text @click="reset" :disabled="!isDirty">
        <v-icon left small>mdi-undo</v-icon>
        Annuler
      </v-btn>
      <v-btn color="#e2007a" dark :disabled="!isDirty" @click="onSave">
        <v-icon left small>mdi-content-save-outline</v-icon>
        Enregistrer
      </v-btn>
    </div>
  </v-form>
</template>

<script>
// Édition multi-section d'un BDC. Émet un diff par section ; le serveur exige
// au moins une section modifiée. RPPS, nom, prénom restent en lecture seule
// (figés par la CPS). L'effacement d'un champ rempli est ignoré côté diff
// (le Joi serveur rejette null/'').

import axios from "axios";
import server from "@/store/server";
import AidesDeMinimisEditor from "@/components/Segur/AidesDeMinimisEditor.vue";

const API_BASE = server.url;

// Champs éditables par section, alignés sur ce que le listing expose et le
// Joi serveur accepte. effectifRef est exposé pour pré-remplir le mode MSP
// et permettre à l'opérateur de le modifier (utile pour transformation).
// montantSegurHT est affiché (readonly) mais exclu du payload PATCH : recalculé
// serveur, Joi le rejette en .forbidden().
const EDITABLE_FIELDS = {
  orderForm: [
    "typePrestation",
    "dateSignatureCommande",
    "codePostalMedecin",
    "aidesPercuesBool",
    "aidesDeMinimis",
  ],
  resident: ["email", "phone", "address", "zip", "city"],
  msp: ["FINESS", "FINESSGEO", "raisonSociale", "effectifRef", "partnerRef"],
};

function emptyForm() {
  return {
    orderForm: {
      montantSegurHT: null,
      typePrestation: null,
      dateSignatureCommande: null,
      codePostalMedecin: null,
      aidesPercuesBool: null,
      aidesDeMinimis: [],
    },
    resident: {
      email: null,
      phone: null,
      address: null,
      zip: null,
      city: null,
    },
    msp: {
      FINESS: null,
      FINESSGEO: null,
      raisonSociale: null,
      effectifRef: null,
      partnerRef: null,
    },
  };
}

// Retourne YYYY-MM-DD a partir d'un Date / string ISO / null
function toDateString(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch (e) {
    return null;
  }
}

// null, undefined et '' sont considérés équivalents : évite un faux diff
// quand un champ vide en DB passe d'undefined à '' sous le focus Vuetify.
function isSameValue(a, b) {
  const norm = (v) => (v === undefined || v === "" ? null : v);
  return norm(a) === norm(b);
}

export default {
  name: "EditableBdcCard",

  components: { AidesDeMinimisEditor },

  props: {
    // BDC objet retourne par le listing /segur/v2/orderforms (incluant resident, msp)
    bdc: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      valid: true,
      dateMenu: false,
      // partyType : 'liberal' | 'msp'. Initialisé au hydrate selon hasMsp.
      // Toggle UI déclenche transformation côté serveur (section partyTransform
      // dans le payload, recalcul typePrestation/montantSegurHT/aides).
      partyType: "liberal",
      form: { ...emptyForm(), partyType: "liberal" },
      snapshot: { ...emptyForm(), partyType: "liberal" },
      // État lookup MSPList par FINESS (pré-remplissage automatique).
      finessLoading: false,
      mspChecked: false,
      mspFound: false,
      rules: {
        montantSegurHT: [
          (v) =>
            v === null ||
            v === undefined ||
            v === "" ||
            Number(v) > 0 ||
            "Le montant doit etre positif",
          (v) => {
            if (v === null || v === undefined || v === "") return true;
            const s = String(v);
            const decimals = (s.split(".")[1] || "").length;
            return decimals <= 2 || "2 décimales maximum";
          },
        ],
        dateSignatureCommande: [
          (v) =>
            !v ||
            /^\d{4}-\d{2}-\d{2}$/.test(v) ||
            "Format date invalide (YYYY-MM-DD)",
          (v) =>
            !v ||
            v <= new Date().toISOString().slice(0, 10) ||
            "La date de signature ne peut pas être dans le futur",
        ],
        codePostal: [
          (v) => !v || /^\d{5}$/.test(v) || "Code postal : 5 chiffres",
        ],
        email: [
          (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || "Email invalide",
        ],
        phone: [
          (v) => !v || (v.length >= 3 && v.length <= 50) || "3 à 50 caractères",
        ],
        address: [
          (v) =>
            !v || (v.length >= 1 && v.length <= 255) || "1 à 255 caractères",
        ],
        city: [
          (v) =>
            !v || (v.length >= 1 && v.length <= 100) || "1 à 100 caractères",
        ],
        finess: [
          (v) => !v || /^\d{9}$/.test(v) || "FINESS : exactement 9 chiffres",
        ],
        finessRequired: [
          (v) => !!v || "FINESS requis pour la transformation en MSP",
          (v) => /^\d{9}$/.test(v || "") || "FINESS : exactement 9 chiffres",
        ],
        raisonSociale: [
          (v) =>
            !v || (v.length >= 1 && v.length <= 255) || "1 à 255 caractères",
        ],
        raisonSocialeRequired: [
          (v) => !!v || "Raison sociale requise pour la transformation en MSP",
          (v) =>
            (v && v.length >= 1 && v.length <= 255) || "1 à 255 caractères",
        ],
        effectif: [
          (v) =>
            v === null ||
            v === undefined ||
            v === "" ||
            (Number.isInteger(Number(v)) && Number(v) >= 1) ||
            "Effectif : entier ≥ 1",
        ],
        effectifRequired: [
          (v) =>
            (v !== null && v !== undefined && v !== "") ||
            "Effectif requis pour la transformation en MSP",
          (v) =>
            (Number.isInteger(Number(v)) && Number(v) >= 1) ||
            "Effectif : entier ≥ 1",
        ],
      },
    };
  },

  computed: {
    hasMsp() {
      return !!(this.bdc && (this.bdc.msp || this.bdc.mspID));
    },

    // Identifiant interne partenaire (ICT) : édité uniquement sous ce flag.
    isPartnerRefEnabled() {
      return (
        this.$store.getters["authentification/getPartnerFeatures"]
          ?.partnerRefEnabled === true
      );
    },

    // Borne haute du date-picker : empêche la saisie d'une date de signature future.
    todayIso() {
      return new Date().toISOString().slice(0, 10);
    },

    // Type de structure initial dérivé du BDC chargé. Référence pour
    // détecter qu'une transformation a été demandée (toggle changé).
    initialPartyType() {
      return this.hasMsp ? "msp" : "liberal";
    },

    // True si le toggle a été basculé par rapport à l'état initial.
    isPartyTransform() {
      return this.form.partyType !== this.initialPartyType;
    },

    // Montant affiché : en transformation, on masque la valeur DB (trompeuse car
    // recalculée serveur). Sinon on affiche la valeur actuelle.
    montantAfficheHT() {
      if (this.isPartyTransform) return "(sera recalculé automatiquement)";
      return this.form.orderForm.montantSegurHT;
    },

    montantHint() {
      if (this.isPartyTransform) {
        return "Transformation détectée : montant recalculé selon la nouvelle structure";
      }
      if (this.form.partyType === "msp") {
        return "Calculé selon la tranche structure - recalculé à la re-signature";
      }
      return "Recalculé automatiquement à la re-signature";
    },

    typePrestationLabel() {
      return this.form.partyType === "msp"
        ? "Type de prestation (Structure)"
        : "Type de prestation (Libéral)";
    },

    // Lock du type par configuration partenaire. Liste maintenue en dur côté front
    // (la source de vérité est partner.features.lockTypePrestation côté serveur,
    // qui rejette le PATCH avec un 400 si l'opérateur force).
    lockTypePrestation() {
      const LOCKED = ["calimed-easycare", "calimed-sante"];
      return LOCKED.includes(this.bdc?.partnerCode);
    },

    // Hint contextuel sans bloquer l'édition : l'opérateur reste libre de switcher
    // V2 ↔ V1V2, le serveur recalculera de toute façon le montant à la re-signature.
    typePrestationHint() {
      if (this.lockTypePrestation) return "Type verrouillé pour ce partenaire";
      if (this.isPartyTransform) return "Déterminé automatiquement après transformation";
      if (this.form.partyType === "msp") return "Montant dépend de la tranche structure";
      return "";
    },

    // Options dynamiques : les montants entre parenthèses ne sont pertinents
    // qu'en libéral (fixes). En MSP les montants dépendent de la tranche.
    typePrestationOptions() {
      if (this.form.partyType === "msp") {
        return [
          { text: "V2 (Structure)", value: "V2" },
          { text: "V1V2 (Structure)", value: "V1V2" },
        ];
      }
      return [
        { text: "V2 (376.25 € HT)", value: "V2" },
        { text: "V1V2 (483.75 € HT)", value: "V1V2" },
      ];
    },

    // True si les 2 codes postaux (TVA médecin vs adresse cabinet) sont remplis
    // au format 5 chiffres et diffèrent. La divergence est légitime (médecin DOM
    // avec cabinet métropole, multi-sites...) mais souvent une saisie incomplète
    // à corriger : on signale visuellement et on propose la copie.
    codesPostauxDiffer() {
      const tva = this.form.orderForm.codePostalMedecin;
      const cabinet = this.form.resident.zip;
      const re = /^\d{5}$/;
      if (!tva || !cabinet) return false;
      if (!re.test(tva) || !re.test(cabinet)) return false;
      return tva !== cabinet;
    },

    // Diff par section. Une section vide est omise (le serveur exige .or()).
    // Un champ texte vidé est ignoré (Joi rejette null/''). Cas spéciaux pour
    // les aides : aidesPercuesBool=false est une valeur légitime, et un passage
    // true-->false force aidesDeMinimis: [] dans le payload pour éviter qu'un
    // ancien tableau reste en DB et fasse échouer le validateur ASP.
    //
    // Si partyType a été basculé : ajout d'une section partyTransform et
    // suppression de la section msp (mutuellement exclusives, le serveur
    // reconstruit le mspID via findOrCreate).
    diff() {
      const out = {};
      for (const section of Object.keys(EDITABLE_FIELDS)) {
        // Section msp : visible uniquement en mode MSP courant.
        if (section === "msp" && this.form.partyType !== "msp") continue;
        const sectionDiff = {};
        for (const field of EDITABLE_FIELDS[section]) {
          const v = this.form[section][field];
          // aidesPercuesBool : accepter false explicite, ne pas filtrer.
          if (field === "aidesPercuesBool") {
            if (v !== this.snapshot[section][field]) {
              sectionDiff[field] = v;
            }
            continue;
          }
          // aidesDeMinimis : tableau, comparer profondément (JSON), accepter [].
          if (field === "aidesDeMinimis") {
            const before = JSON.stringify(this.snapshot[section][field] || []);
            const after = JSON.stringify(Array.isArray(v) ? v : []);
            if (before !== after) {
              sectionDiff[field] = Array.isArray(v) ? v : [];
            }
            continue;
          }
          // partnerRef : autoriser l'effacement (vidé alors que le snapshot était
          // renseigné -> null explicite, sinon le champ vide serait ignoré).
          if (field === "partnerRef") {
            const after = v === null || v === undefined || v === "" ? null : v;
            const before = this.snapshot[section][field] || null;
            if (after !== before) {
              sectionDiff[field] = after;
            }
            continue;
          }
          const isEmpty = v === null || v === undefined || v === "";
          if (isEmpty) continue;
          if (!isSameValue(v, this.snapshot[section][field])) {
            sectionDiff[field] = v;
          }
        }
        // Force true-->false : si aidesPercuesBool passe à false, on envoie aussi
        // aidesDeMinimis: [] même si le tableau n'a pas été touché côté UI.
        if (
          sectionDiff.aidesPercuesBool === false &&
          !Object.prototype.hasOwnProperty.call(sectionDiff, "aidesDeMinimis")
        ) {
          sectionDiff.aidesDeMinimis = [];
        }
        if (Object.keys(sectionDiff).length > 0) {
          out[section] = sectionDiff;
        }
      }

      // Transformation libéral viceversa MSP : ajoute partyTransform et retire la
      // section msp (le serveur reconstruit mspID + recalcule tarif). Les
      // champs aides côté orderForm seront aussi écrasés serveur (null/false
      // selon target) — on les filtre du diff pour éviter un conflit.
      if (this.isPartyTransform) {
        if (this.form.partyType === "msp") {
          out.partyTransform = {
            target: "msp",
            finess: this.form.msp.FINESS,
            finessGeo: this.form.msp.FINESSGEO,
            raisonSociale: this.form.msp.raisonSociale,
            effectifRef: Number(this.form.msp.effectifRef),
          };
        } else {
          out.partyTransform = { target: "liberal" };
        }
        delete out.msp;
        if (out.orderForm) {
          // typePrestation et aides : recalculés serveur après transformation,
          // on les filtre pour ne pas envoyer une valeur que l'UI affiche déjà comme stale.
          delete out.orderForm.typePrestation;
          delete out.orderForm.aidesPercuesBool;
          delete out.orderForm.aidesDeMinimis;
          if (Object.keys(out.orderForm).length === 0) delete out.orderForm;
        }
      }

      return out;
    },

    isDirty() {
      return Object.keys(this.diff).length > 0;
    },
  },

  // Token de séquence pour le lookup FINESS (anti race condition saisie rapide).
  // Pas dans data() : ne doit pas être réactif (pas de re-render à chaque tick).
  created() {
    this._finessLookupToken = 0;
  },

  watch: {
    bdc: {
      immediate: true,
      handler() {
        this.hydrate();
      },
    },

    // Lookup MSPList déclenché à chaque saisie FINESS de 9 chiffres.
    // Préremplit FINESSGEO / raisonSociale / effectifRef SI VIDES (ne jamais
    // écraser ce que l'opérateur a saisi manuellement).
    "form.msp.FINESS"(val) {
      if (this.form.partyType !== "msp") return;
      if (val && /^\d{9}$/.test(val)) {
        this.lookupMspByFiness(val);
      } else {
        this.mspChecked = false;
        this.mspFound = false;
      }
    },
  },

  methods: {
    // Libellé lisible pour la bannière warning (libéral / MSP au lieu de liberal/msp).
    partyTypeLabel(type) {
      if (type === "msp") return "MSP";
      if (type === "liberal") return "libéral";
      return type || "";
    },

    // Asterisque visuel sur les labels des champs MSP en mode transformation
    // (le serveur exige ces champs via Joi, l'opérateur doit les saisir).
    finessLabel(label) {
      return this.isPartyTransform ? `${label} *` : label;
    },

    // Copie unidirectionnelle codePostalMedecin (TVA) vers Resident.zip (cabinet).
    // L'opérateur garde la main : aucune sync auto, juste un raccourci.
    copyTvaVersCabinet() {
      this.form.resident.zip = this.form.orderForm.codePostalMedecin;
    },

    copyCabinetVersTva() {
      this.form.orderForm.codePostalMedecin = this.form.resident.zip;
    },

    /**
     * Lookup MSPList par FINESS avec token de séquence pour éviter les race
     * conditions (saisie rapide = plusieurs requêtes concurrentes). Pré-remplit
     * uniquement les champs VIDES côté UI : si l'opérateur a déjà saisi un
     * FINESSGEO ou une raison sociale, on ne l'écrase pas.
     */
    async lookupMspByFiness(finess) {
      const token = ++this._finessLookupToken;
      this.finessLoading = true;
      this.mspChecked = false;
      try {
        const res = await axios.get(
          `${API_BASE}/segur/msp-by-finess/${finess}`,
          { headers: server.getHeader() },
        );
        if (token !== this._finessLookupToken) return;
        const data = res.data?.data;
        const found = !!(data && (data.found || data.raisonSociale));
        this.mspFound = found;
        // Pré-remplissage uniquement des champs vides : ne pas écraser une
        // saisie manuelle de l'opérateur (qui peut corriger les valeurs ANS).
        if (data) {
          if (!this.form.msp.FINESSGEO && data.FINESSGEO) {
            this.form.msp.FINESSGEO = data.FINESSGEO;
          }
          if (!this.form.msp.raisonSociale && data.raisonSociale) {
            this.form.msp.raisonSociale = data.raisonSociale;
          }
          if (
            (this.form.msp.effectifRef == null ||
              this.form.msp.effectifRef === "") &&
            data.effectifRef
          ) {
            this.form.msp.effectifRef = data.effectifRef;
          }
        }
      } catch (e) {
        if (token !== this._finessLookupToken) return;
        this.mspFound = false;
      } finally {
        if (token === this._finessLookupToken) {
          this.mspChecked = true;
          this.finessLoading = false;
        }
      }
    },

    hydrate() {
      const snap = emptyForm();
      const b = this.bdc || {};

      // OrderForm : champs exposes par le listing /segur/v2/orderforms
      snap.orderForm.montantSegurHT =
        b.montantSegurHT != null ? Number(b.montantSegurHT) : null;
      snap.orderForm.typePrestation = b.typePrestation || null;
      snap.orderForm.dateSignatureCommande = toDateString(
        b.dateSignatureCommande,
      );
      snap.orderForm.codePostalMedecin = b.codePostalMedecin || null;
      // Aides de minimis : aidesPercuesBool source unique, fallback aidesPercues
      // legacy si le row mapping n'expose pas encore la nouvelle colonne.
      snap.orderForm.aidesPercuesBool =
        b.aidesPercuesBool === true
          ? true
          : b.aidesPercuesBool === false
          ? false
          : b.aidesPercues === true || b.aidesPercues === "true"
          ? true
          : b.aidesPercues === false || b.aidesPercues === "false"
          ? false
          : null;
      // aidesDeMinimis : tableau d'objets, dates en JJ/MM/AAAA pour saisie UI.
      snap.orderForm.aidesDeMinimis = Array.isArray(b.aidesDeMinimis)
        ? b.aidesDeMinimis.map((a) => ({
            dateAttribution: a?.dateAttribution || "",
            montantAides:
              typeof a?.montantAides === "number"
                ? a.montantAides
                : Number(a?.montantAides) || 0,
          }))
        : [];

      const r = b.resident || {};
      snap.resident.email = r.email || null;
      snap.resident.phone = r.phone || null;
      snap.resident.address = r.address || null;
      snap.resident.zip = r.zip || null;
      snap.resident.city = r.city || null;

      // MSP (si BDC structure). effectifRef expose par le listing pour
      // pre-remplir et permettre l'edition (sert aussi a la transformation
      // libéral --> MSP qui derive la tranche cote serveur).
      const m = b.msp || {};
      snap.msp.FINESS = m.FINESS || null;
      snap.msp.FINESSGEO = m.FINESSGEO || null;
      snap.msp.raisonSociale = m.raisonSociale || null;
      snap.msp.effectifRef =
        m.effectifRef != null ? Number(m.effectifRef) : null;
      snap.msp.partnerRef = m.partnerRef || null;

      // partyType : initialise selon hasMsp. Le toggle UI bascule librement.
      const initialType = this.hasMsp ? "msp" : "liberal";
      snap.partyType = initialType;

      this.snapshot = snap;
      this.form = JSON.parse(JSON.stringify(snap));
    },

    reset() {
      this.form = JSON.parse(JSON.stringify(this.snapshot));
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
      this.$emit("cancel");
    },

    onSave() {
      // Validation Vuetify avant emission (mireur Joi serveur)
      const ok = this.$refs.form ? this.$refs.form.validate() : true;
      if (!ok) return;
      if (!this.isDirty) return;
      this.$emit("save", this.diff);
    },
  },
};
</script>

<style scoped>
.section-title {
  font-size: 14px;
  font-weight: bold;
  padding: 8px 16px;
}
/* Hint discret sous les radios libéral/MSP */
.party-transform-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #ef6c00;
  background: #fff8e1;
  border-left: 3px solid #ffa000;
  padding: 6px 10px;
  border-radius: 3px;
  line-height: 1.4;
}
/* Hint divergence codes postaux TVA / cabinet : même style que la
   transformation pour cohérence visuelle. */
.codes-postaux-mismatch {
  font-size: 12px;
  color: #ef6c00;
  background: #fff8e1;
  border-left: 3px solid #ffa000;
  padding: 6px 10px;
  border-radius: 3px;
  line-height: 1.4;
}
.aides-fieldset {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 12px;
}
.aides-fieldset legend {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  padding: 0 6px;
}
.aides-disabled-note {
  font-size: 11px;
  color: #999;
  font-style: italic;
  margin-top: 4px;
}
</style>
